import {
	EFxIdSdkAdapterInfoSocialCapability,
	FxIdSdkAdapterSocialSettingDefault,
	FxIdSdkBaseAdapter,
	IExportedFxIdSdkMethod,
	IFxIdSdkAdapterBuyProductRequest,
	IFxIdSdkAdapterBuyProductResponse,
	IFxIdSdkAdapterInfoSocial,
	IFxIdSdkAdapterSocialSettings,
	IFxIdSdkAdapterStatEventRequest,
	IFxIdSdkAdapterStatInitializeRequest
} from "./FxIdSdkBaseAdapter";
import { deferred } from "./FxIdSdkUtils";
import OpenApiClient from "../Api/OpenApiClient";
import {
	IVkExternalApi,
	IVkExternalApiError,
	IVkGetStatusCallbackResult,
	IVkIframeApiCallbacks,
	IVkMerchantParams,
	IVkUserInfoCallbackResult,
	VkLoginStatusUserAuthorizedNotRegistered,
	VkLoginStatusUserNotAuthorized
} from "../vkplay";
import {
	FxIdApplicationStoreCreatePaymentHandlerEmbeddingType,
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxGamesLibWebPublicDataBase,
	FxIdWebFeaturesStoreCreatePaymentRequest
} from "../Api/gen";
import { sharingHandlersStore, userStore } from "../Stores";
import i18next from "i18next";
import { getAuthToken } from "@app/Api/Auth";
import { IframeType } from "@app/Screens/WebPlayer/Hooks";
import { FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus } from "../Api/gen";

export class FxIdSdkAdapterForVkPlay extends FxIdSdkBaseAdapter implements IVkIframeApiCallbacks {
	readonly adapter: IframeType = "vkplay";
	readonly defaultSocialSettings: IFxIdSdkAdapterSocialSettings = {
		...FxIdSdkAdapterSocialSettingDefault,
		ui: {
			...FxIdSdkAdapterSocialSettingDefault.ui,
			disabled: false
		}
	};
	vkExternalApi?: IVkExternalApi;
	private buyProductResolve?: (data: any) => void;
	private buyProductReject?: (data: any) => void;
	private userInfoCallbackResolve?: (data: IVkUserInfoCallbackResult) => void;
	private userInfoCallbackReject?: (reason?: IVkExternalApiError) => void;
	private getLoginStatusCallbackResolve?: (data: IVkGetStatusCallbackResult) => void;
	private getLoginStatusCallbackReject?: (reason?: IVkExternalApiError) => void;
	appid!: number;
	private registerUserCallbackResolve?: (data: IVkUserInfoCallbackResult) => void;
	private registerUserCallbackReject?: (reason?: IVkExternalApiError) => void;
	private userInfo?: IFxIdSdkAdapterInfoSocial;
	private currentOrderId?: string;

	public static VkPlayHost = "vkplay.ru";

	constructor(
		protected exportedSdk: IExportedFxIdSdkMethod,
		protected game: string,
		protected config: FxGamesLibWebPublicDataBase
	) {
		super(exportedSdk, game, config);
		const appId = config.PlatformData.VkontaktePlay?.AppId;

		if (appId == null) {
			throw new Error("No AppId for VkontaktePlay adapter");
		}
		this.appid = appId;
	}

	RegisterShareHandlers(): Promise<void> {
		// Это все залипуха. Надо будет менять на нормальные апи
		sharingHandlersStore.getState().enableShareVk(() => {
			const url = `https://vk.com/share.php?url=${window.location}&title=${i18next.t(`seo.${this.game}.title`)}`;
			window.open(url, "__blank");
		});

		sharingHandlersStore.getState().enableShareOk(() => {
			const url = `https://connect.ok.ru/offer?url=${window.location}&title=${i18next.t(
				`seo.${this.game}.title`
			)}`;
			window.open(url, "__blank");
		});

		sharingHandlersStore.getState().enableShareTelegram(() => {
			const url = `https://telegram.me/share/url?url=${window.location}&text=${i18next.t(
				`seo.${this.game}.title`
			)}`;
			window.open(url, "__blank");
		});
		return Promise.resolve();
	}

	async BuyProduct(request: IFxIdSdkAdapterBuyProductRequest): Promise<IFxIdSdkAdapterBuyProductResponse> {
		const url = new URL(window.location.href);
		const currency =
			(url.searchParams.get("currency") as keyof IVkMerchantParams["merchant_param"]["currency"]) ?? "RUB";
		const paymentRequest: FxIdWebFeaturesStoreCreatePaymentRequest = {
			Token: getAuthToken()!,
			Game: this.game,
			Sku: request.sku,
			WebPublishingPlatform: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontaktePlay,
			EmbeddingType: FxIdApplicationStoreCreatePaymentHandlerEmbeddingType.Embed,
			Currency: currency,
			VkontaktePlay: {
				IpAddress: "0.0.0.0",
				Uid: this.userInfo?.userId ?? url.searchParams.get("uid") ?? "",
				Currency: currency
			},
			ProductDescriptionHint: request.productDescriptionHint,
			ProductNameHint: request.productNameHint
		};

		if (request.developerPayload != null) {
			paymentRequest.DeveloperPayload = {
				MerchantDeveloperPayload: request.developerPayload
			};
		}

		const createPaymentResult = await OpenApiClient.Store.fxIdWebFeaturesStoreCreatePaymentEndpoint(paymentRequest);

		log.info("Received result from server: %o", createPaymentResult);

		this.currentOrderId = createPaymentResult.TransactionId;

		const { promise, resolve, reject } = deferred<IFxIdSdkAdapterBuyProductResponse>();
		this.buyProductResolve = async (data: any) => {
			log.info("Buy product resolved %o", data);
			await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint({
				PublicOrderId: createPaymentResult.TransactionId,
				Status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Completed
			}).catch(e => log.error("[VKPLAY] Failed to set optimistic status:", e));

			resolve({
				...data,
				transactionId: createPaymentResult.TransactionId,
				stats: {
					currency: createPaymentResult.OrderProduct.Currency ?? "",
					currencyAmount: createPaymentResult.OrderProduct.Price ?? 0,
					usdAmount: createPaymentResult.OrderProduct.UsdPrice ?? 0
				}
			});
		};
		this.buyProductReject = async (data: any) => {
			log.info("Buy product rejected %o", data);
			await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint({
				PublicOrderId: createPaymentResult.TransactionId,
				Status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Cancelled
			}).catch(e => log.error("[VKPLAY] Failed to set optimistic status:", e));
			reject(new Error(data));
		};

		const paymentFrameParams: IVkMerchantParams = {
			merchant_param: {
				amount: createPaymentResult.OrderPrice,
				description: createPaymentResult.OrderProduct.LocalizedProductDescription,
				currency: currency,
				public_order_id: createPaymentResult.TransactionId
			}
		};

		log.info("Requesting payment frame with params", paymentFrameParams);
		this.vkExternalApi?.paymentFrame(paymentFrameParams);

		return promise;
	}

	GetSocialInfo(): Promise<IFxIdSdkAdapterInfoSocial> {
		const { promise, resolve, reject } = deferred<IFxIdSdkAdapterInfoSocial>();
		this.userInfoCallbackResolve = (data: IVkUserInfoCallbackResult) => {
			resolve({
				social: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontaktePlay,
				paymentsAvailable: true,
				userId: data.uid.toString(),
				capabilities: []
			});
		};
		this.userInfoCallbackReject = reject;

		this.vkExternalApi?.userInfo();

		return promise;
	}

	GetLoginStatus(): Promise<IVkGetStatusCallbackResult> {
		log.info("Requesting login status");
		const { promise, resolve, reject } = deferred<IVkGetStatusCallbackResult>();
		this.getLoginStatusCallbackResolve = resolve;
		this.getLoginStatusCallbackReject = reject;

		this.vkExternalApi?.getLoginStatus();

		return promise;
	}

	RegisterUser(): Promise<IVkUserInfoCallbackResult> {
		log.info("Requesting register user");
		const { promise, resolve, reject } = deferred<IVkUserInfoCallbackResult>();
		this.registerUserCallbackResolve = resolve;
		this.registerUserCallbackReject = reject;

		this.vkExternalApi?.registerUser();

		return promise;
	}

	async Initialize(): Promise<void> {
		log.info("Initializing VkPlay adapter");

		await new Promise<void>((resolve) => {
			const s = document.createElement("script");
			s.type = "text/javascript";
			s.src = `//vkplay.ru/app/${this.appid}/static/mailru.core.js`;
			s.addEventListener(
				"load",
				function (e) {
					log.info("Script loaded");
					resolve();
				},
				false
			);
			const head = document.getElementsByTagName("head")[0];
			head.appendChild(s);

			log.info("Script added");
		});

		log.info("Initializing api");
		this.vkExternalApi = await window.iframeApi({
			appid: this.appid,
			getLoginStatusCallback: this.getLoginStatusCallback.bind(this),
			userInfoCallback: this.userInfoCallback.bind(this),
			userProfileCallback: this.userProfileCallback.bind(this),
			registerUserCallback: this.registerUserCallback.bind(this),
			paymentFrameUrlCallback: this.paymentFrameUrlCallback.bind(this),
			getAuthTokenCallback: this.getAuthTokenCallback.bind(this),
			paymentReceivedCallback: this.paymentReceivedCallback.bind(this),
			paymentWindowClosedCallback: this.paymentWindowClosedCallback.bind(this),
			userConfirmCallback: this.userConfirmCallback.bind(this)
		});
		log.info("Api initialized");

		const loginStatus = await this.GetLoginStatus();

		log.info("Received login status: " + loginStatus);

		if (loginStatus.loginStatus === VkLoginStatusUserNotAuthorized) {
			this.vkExternalApi.authUser();

			// https://documentation.vkplay.ru/f2p_vkp/f2pb_js_vkp#authUser
			// Авторизация пользователя. При вызове метода появляется окно авторизации.
			// После авторизации страница будет перезагружена, далее можно запросить статус и информацию о пользователе.
			await new Promise((resolve) => {
				log.info("Eternal promise. VkPlay MUST reload page");
			});
			document.location.reload();
		} else if (loginStatus.loginStatus === VkLoginStatusUserAuthorizedNotRegistered) {
			await this.RegisterUser();
			this.vkExternalApi?.reloadWindow();
			document.location.reload();
		}

		this.userInfo = await this.GetSocialInfo();

		userStore.getState().updateUserId(this.userInfo.userId);
		userStore.getState().updateDisplayName(`#${this.userInfo.userId}`);
	}

	StoreCurrency(): Promise<string | undefined> {
		const url = new URL(window.location.href);
		const currency =
			(url.searchParams.get("currency") as keyof IVkMerchantParams["merchant_param"]["currency"]) ?? "RUB";

		return Promise.resolve(currency);
	}

	paymentReceivedCallback = (data: { uid: string }): void => {
		log.info("paymentReceivedCallback");
		this.buyProductResolve?.call(this, { transactionId: data.uid });
	};

	paymentWindowClosedCallback = (): void => {
		log.info("paymentWindowClosedCallback");
		this.buyProductReject?.call(this, "User closed payment window");
	};

	userInfoCallback = (param: IVkUserInfoCallbackResult | IVkExternalApiError): void => {
		log.info("userInfoCallback", param);
		if (param.status === "error") {
			this.userInfoCallbackReject?.call(this, param);
		} else {
			this.userInfoCallbackResolve?.call(this, param);
		}
	};

	getLoginStatusCallback = (param: IVkGetStatusCallbackResult | IVkExternalApiError): void => {
		log.info("getLoginStatusCallback", param);
		if (param.status === "error") {
			this.getLoginStatusCallbackReject?.call(this, param);
		} else {
			this.getLoginStatusCallbackResolve?.call(this, param);
		}
	};

	registerUserCallback = (param: IVkUserInfoCallbackResult | IVkExternalApiError): void => {
		log.info("registerUserCallback", param);
		if (param.status === "error") {
			this.registerUserCallbackReject?.call(this, param);
		} else {
			this.registerUserCallbackResolve?.call(this, param);
		}
	};

	userProfileCallback = (profile: any) => {
		log.info("userProfileCallback", profile);
	};

	paymentFrameUrlCallback = (url: any): void => {
		log.info("paymentFrameUrlCallback", url);
	};

	getAuthTokenCallback = (token: string): void => {
		log.info("getAuthTokenCallback", token);
	};

	userConfirmCallback = (): void => {
		log.info("userConfirmCallback");
	};

	StatInitialize(request: IFxIdSdkAdapterStatInitializeRequest): Promise<void> {
		return Promise.resolve(undefined);
	}

	StatEvent(request: IFxIdSdkAdapterStatEventRequest): Promise<void> {
		return Promise.resolve(undefined);
	}
}
